import { store } from '@/store'
import { employeeRoleApi } from '@/store/services'
import { toOptions } from '@/utils'
import { useEffect, useState } from 'react'
import { AsyncPaginate } from 'react-select-async-paginate'
import { SelectWrapper } from '../wrapper'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

export const EmployeeRoleSelector = ({
  value,
  onChange,
  required = false,
  serviceAreaIds = [],
  disabled = false,
  onLoadingChange
}) => {
  const { t } = useTranslation()
  const [selectedOption, setSelectedOption] = useState({})
  const serviceAreaFilterKey = serviceAreaIds.join(',')
  const loadOptions = async (search, _loadedOptions, { page }) => {
    const searchParams = new URLSearchParams({ page, per_page: 10 })

    if (search) searchParams.set('q', search)
    serviceAreaIds.forEach((id) => searchParams.append('service_area_ids[]', id))

    const { employeeRoles, meta } = await store.dispatch(
      employeeRoleApi.endpoints.getEmployeeRoles.initiate(`?${searchParams.toString()}`)
    ).unwrap()

    const options = toOptions({ data: employeeRoles })
    const hasMore = page < meta.pagination.total_pages

    return {
      options,
      hasMore,
      additional: {
        page: page + 1
      }
    }
  }

  const getCurrentEmployeeRole = async (id) => {
    onLoadingChange?.(true)

    try {
      const { employeeRole = {} } = await store.dispatch(
        employeeRoleApi.endpoints.getEmployeeRole.initiate(id)
      ).unwrap()

      setSelectedOption(toOptions({ data: [employeeRole] })[0])
    } finally {
      onLoadingChange?.(false)
    }
  }

  // Si el selector esta en un popover o modal, se pierde el selectedOption
  // Esta es una forma para rescatarlo si es que se pierde el estado.
  useEffect(() => {
    if (!value) {
      setSelectedOption({})
      return
    }

    getCurrentEmployeeRole(value)
  }, [value, serviceAreaFilterKey])

  const handleChange = (option) => {
    setSelectedOption(option)
    onChange(option)
  }

  return (
    <SelectWrapper label={t('role.one')} required={required}>
      {(styles) => (
        <AsyncPaginate
          value={Object.keys(selectedOption).length ? selectedOption : null}
          debounceTimeout={600}
          placeholder={t('select.one')}
          isDisabled={disabled}
          onChange={handleChange}
          loadOptions={loadOptions}
          cacheUniqs={[serviceAreaFilterKey]}
          styles={styles}
          menuPortalTarget={document.body}
          loadingMessage={() => t('select.loading')}
          additional={{
            page: 1
          }}
        />
      )
      }
    </SelectWrapper>
  )
}

EmployeeRoleSelector.propTypes = {
  onChange: PropTypes.func,
  required: PropTypes.bool,
  value: PropTypes.number,
  serviceAreaIds: PropTypes.arrayOf(PropTypes.number),
  disabled: PropTypes.bool,
  onLoadingChange: PropTypes.func
}
